import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SVGAPlayer from '../../../components/SVGAPlayer';
import WebPPlayer from '../../../components/WebPPlayer';
import AnimatedImagePlayer from '../../../components/AnimatedImagePlayer';
import SVGAModal from '../../../components/SVGAModal';
import WebPModal from '../../../components/WebPModal';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

const AnimatedImageDisplay = ({ 
    animatedUrl, 
    giftName, 
    size = 'small',
    showButton = true 
}) => {
    const [modalState, setModalState] = useState({
        isOpen: false,
        type: null, // 'svga', 'webp', or 'gif'
        animatedUrl: '',
        giftName: ''
    });

    const detectFileType = (url) => {
        if (!url) return null;
        const extension = url.split('.').pop()?.toLowerCase();
        
        if (extension === 'svga') {
            return 'svga';
        } else if (extension === 'webp') {
            return 'webp';
        } else if (extension === 'gif') {
            return 'gif';
        }
        
        return null;
    };

    const openModal = (url, name) => {
        const fileType = detectFileType(url);
        
        if (!fileType) {
            return;
        }
        
        setModalState({
            isOpen: true,
            type: fileType,
            animatedUrl: url,
            giftName: name
        });
    };

    const closeModal = () => {
        setModalState({
            isOpen: false,
            type: null,
            animatedUrl: '',
            giftName: ''
        });
    };

    const handleImageClick = (data) => {
        openModal(animatedUrl, giftName);
    };

    const getSizeStyles = () => {
        switch (size) {
            case 'small':
                return { width: '50px', height: '50px' };
            case 'medium':
                return { width: '100px', height: '100px' };
            case 'large':
                return { width: '100px', height: '100px' };
            default:
                return { width: '50px', height: '50px' };
        }
    };

    if (!animatedUrl) {
        const sizeStyles = getSizeStyles();
        return (
            <div style={{ 
                ...sizeStyles,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontStyle: 'italic',
                fontSize: '10px',
                textAlign: 'center',
                background: '#f8f9fa',
                borderRadius: '4px',
                border: '1px dashed #ddd'
            }}>
                Không có
            </div>
        );
    }

    const fileType = detectFileType(animatedUrl);
    const sizeStyles = getSizeStyles();

    return (
        <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                {fileType === 'svga' ? (
                    <div style={{ position: 'relative' }}>
                        <SVGAPlayer
                            animatedUrl={animatedUrl}
                            giftName={giftName}
                            className={size}
                            width={sizeStyles.width}
                            height={sizeStyles.height}
                            showControls={false}
                            onClick={handleImageClick}
                        />
                    </div>
                ) : fileType === 'webp' ? (
                    <WebPPlayer
                        animatedUrl={animatedUrl}
                        giftName={giftName}
                        className={size}
                        width={sizeStyles.width}
                        height={sizeStyles.height}
                        showButton={false}
                        onClick={handleImageClick}
                    />
                ) : fileType === 'gif' ? (
                    <div style={{ position: 'relative' }}>
                        <AnimatedImagePlayer
                            animatedUrl={animatedUrl}
                            giftName={giftName}
                            width={sizeStyles.width}
                            height={sizeStyles.height}
                            showControls={false}
                            onClick={handleImageClick}
                        />
                    </div>
                ) : (
                    <div style={{ 
                        ...sizeStyles,
                        background: '#f0f0f0',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        color: '#666',
                        flexDirection: 'column',
                        gap: '2px'
                    }}>
                        <span>{fileType || 'Unknown'}</span>
                        <span style={{ fontSize: '8px' }}>format</span>
                    </div>
                )}
                
                {showButton && fileType && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openModal(animatedUrl, giftName)}
                        style={{ 
                            fontSize: '10px', 
                            padding: '4px 8px',
                            minWidth: '60px'
                        }}
                    >
                        {fileType === 'svga' ? '🎬 Xem' : fileType === 'webp' ? '🖼️ Xem' : fileType === 'gif' ? '🎭 Xem' : '👁️ Xem'}
                    </Button>
                )}
            </div>

            {/* SVGA Modal */}
            {modalState.isOpen && modalState.type === 'svga' && (
                <SVGAModal
                    isOpen={modalState.isOpen}
                    onClose={closeModal}
                    animatedUrl={modalState.animatedUrl}
                    giftName={modalState.giftName}
                    size="large"
                />
            )}

            {/* WebP Modal */}
            {modalState.isOpen && modalState.type === 'webp' && (
                <WebPModal
                    isOpen={modalState.isOpen}
                    onClose={closeModal}
                    animatedUrl={modalState.animatedUrl}
                    giftName={modalState.giftName}
                    size="large"
                />
            )}

            {/* GIF Modal - Sử dụng AnimatedImagePlayer trong modal */}
            {modalState.isOpen && modalState.type === 'gif' && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        padding: '20px'
                    }}
                    onClick={closeModal}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            position: 'relative'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '15px',
                            cursor: 'pointer',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#666',
                            zIndex: 1
                        }}
                        onClick={closeModal}
                        >
                            ×
                        </div>
                        <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                            <Typography variant="h6" style={{ fontWeight: 'bold', color: '#333' }}>
                                {modalState.giftName}
                            </Typography>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <AnimatedImagePlayer
                                animatedUrl={modalState.animatedUrl}
                                giftName={modalState.giftName}
                                width="300px"
                                height="300px"
                                showControls={true}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Debug info */}
            {modalState.isOpen && (
                <div style={{ 
                    position: 'fixed', 
                    top: '10px', 
                    right: '10px', 
                    background: 'rgba(0,0,0,0.8)', 
                    color: 'white', 
                    padding: '10px', 
                    borderRadius: '5px',
                    fontSize: '12px',
                    zIndex: 10000
                }}>
                    Modal Debug: {modalState.type} - {modalState.animatedUrl}
                </div>
            )}
        </>
    );
};

AnimatedImageDisplay.propTypes = {
    animatedUrl: PropTypes.string,
    giftName: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    showButton: PropTypes.bool
};

AnimatedImageDisplay.defaultProps = {
    size: 'small',
    showButton: true
};

export default AnimatedImageDisplay; 